import React from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ComplianceSlider from "../ComplianceSlider";
import styles from "../insuranceStatement.module.css";

export const BILLING = {
  LAST_30: "last30",
  PREV_MONTH: "prevMonth",
  CUSTOMIZE: "customize",
};

export const BILLING_WINDOW_TYPE = {
  [BILLING.LAST_30]: "last_30_days",
  [BILLING.PREV_MONTH]: "previous_calendar_month",
  [BILLING.CUSTOMIZE]: "custom_last_n_days",
};

export function buildInsuranceStatementPayload({ billingWindow, complianceDays, customDays }) {
  const payload = {
    billing_window_type: BILLING_WINDOW_TYPE[billingWindow],
    compliance_threshold: complianceDays,
  };
  if (billingWindow === BILLING.CUSTOMIZE) {
    payload.last_n_days = customDays;
  }
  return payload;
}

// Real start/end Date objects for a given billing window, computed from the current date —
// the single source of truth shared by the on-screen labels and the API payload dates.
export function getBillingWindowRange({ billingWindow, customDays }) {
  const end = new Date();

  if (billingWindow === BILLING.PREV_MONTH) {
    const firstOfThisMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    const lastOfPrevMonth = new Date(firstOfThisMonth.getTime() - 1);
    const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);
    return { start: firstOfPrevMonth, end: lastOfPrevMonth };
  }

  const windowDays = billingWindow === BILLING.CUSTOMIZE ? customDays : 30;
  const start = new Date(end);
  start.setDate(start.getDate() - windowDays);
  return { start, end };
}

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDisplayDate(date) {
  return `${date.getFullYear()} ${MONTH_ABBR[date.getMonth()]} ${date.getDate()}`;
}

export function formatBillingWindowRange({ billingWindow, customDays }) {
  const { start, end } = getBillingWindowRange({ billingWindow, customDays });
  const endStr =
    start.getFullYear() === end.getFullYear()
      ? `${MONTH_ABBR[end.getMonth()]} ${end.getDate()}`
      : formatDisplayDate(end);
  return `${formatDisplayDate(start)} - ${endStr}`;
}

function RadioDot({ selected }) {
  return (
    <div
      className={`${styles.radioDot} ${selected ? styles.radioDotSelected : styles.radioDotDefault}`}
    >
      {selected && <div className={styles.radioDotInner} />}
    </div>
  );
}

function BillingOption({ selected, onClick, className, children, right }) {
  return (
    <div
      onClick={onClick}
      className={`${className || styles.billingOption} ${selected ? styles.billingOptionSelected : styles.billingOptionDefault}`}
    >
      <div className={styles.billingOptionLeft}>
        <RadioDot selected={selected} />
        {children}
      </div>
      {right}
    </div>
  );
}

function DaySelector({ value, onChange }) {
  const stop = (e) => e.stopPropagation();

  return (
    <div className={styles.daySelectionWrapper} onClick={stop}>
      <div className={styles.daySelection}>
        <button
          type="button"
          className={styles.daySelectionBtn}
          onClick={() => onChange(Math.max(30, value - 1))}
        >
          -
        </button>
        <span className={styles.daySelectionValue}>{value}</span>
        <button
          type="button"
          className={styles.daySelectionBtn}
          onClick={() => onChange(Math.min(60, value + 1))}
        >
          +
        </button>
      </div>
      <span className={styles.billingOptionLabel}>days</span>
    </div>
  );
}

export default function TimeWindowStep({
  complianceDays,
  setComplianceDays,
  billingWindow,
  setBillingWindow,
  customDays,
  setCustomDays,
}) {
  const last30Range = formatBillingWindowRange({ billingWindow: BILLING.LAST_30 });
  const prevMonthRange = formatBillingWindowRange({ billingWindow: BILLING.PREV_MONTH });
  const customRange = formatBillingWindowRange({ billingWindow: BILLING.CUSTOMIZE, customDays });

  const billingLabel =
    billingWindow === BILLING.LAST_30
      ? `Last 30 days (${last30Range})`
      : billingWindow === BILLING.PREV_MONTH
      ? `Previous calendar month (${prevMonthRange})`
      : `Custom period (${customRange})`;

  return (
    <div className={styles.stepContainer}>
      {/* Compliance threshold */}
      <div>
        <div className={styles.sectionLabel}>Compliance threshold</div>
        <ComplianceSlider value={complianceDays} onChange={setComplianceDays} />
      </div>

      {/* Billing window */}
      <div>
        <div className={styles.sectionLabel}>Billing window</div>

        <div className={styles.billingOptionsRow}>
          <BillingOption
            selected={billingWindow === BILLING.LAST_30}
            onClick={() => setBillingWindow(BILLING.LAST_30)}
          >
            <div>
              <div className={styles.billingOptionLabel}>Last 30 days</div>
              <div className={styles.textSmall}>{last30Range}</div>
            </div>
          </BillingOption>

          <BillingOption
            selected={billingWindow === BILLING.PREV_MONTH}
            onClick={() => setBillingWindow(BILLING.PREV_MONTH)}
          >
            <div>
              <div className={styles.billingOptionLabel}>Previous calendar month</div>
              <div className={styles.textSmall}>{prevMonthRange}</div>
            </div>
          </BillingOption>
        </div>

        <BillingOption
          selected={billingWindow === BILLING.CUSTOMIZE}
          onClick={() => setBillingWindow(BILLING.CUSTOMIZE)}
          className={styles.billingOptionCustomize}
          right={
            billingWindow === BILLING.CUSTOMIZE && (
              <DaySelector value={customDays} onChange={setCustomDays} />
            )
          }
        >
          <span className={styles.billingOptionLabel}>Customize - last N days</span>
        </BillingOption>
      </div>

      {/* Eligibility rule */}
      <div className={styles.eligibilityBox}>
        <div className={styles.eligibilityIconCol}>
          <CheckCircleOutlineIcon style={{ color: "#197f8f", fontSize: 24 }} />
          <div className={styles.eligibilityIconLabel}>
            Eligibility rule
          </div>
        </div>

        <div>
          <div className={styles.eligibilityBodyText}>
            A patient is eligible after using their device for at least{" "}
            <span className={styles.eligibilityHighlight}>{complianceDays} days</span>
          </div>
          <div className={styles.eligibilityBodyText}>
            in the{" "}
            <span className={styles.eligibilityHighlight}>{billingLabel}</span>.
          </div>
        </div>
      </div>
    </div>
  );
}
